import type { AsyncStatus } from 'lib-shared-legacy-types';
import { Action, handleActions } from 'redux-actions';

import type { AudioT } from 'components/shared/audio/api/types';

import type {
  FlowCallGetAllMusicResponseT,
  FlowCallMusicSplitResponseT,
  FlowCallWaitMusicPayloadT,
} from '../../holdMusic/components/types';
import { waitMusicActions } from '../actions/waitMusicActions';

export type WaitMusicStateT = {
  allAudios: AudioT[];
  jobIds: string[];
  meta: WaitMusicMetaT;
  selectedAudio: AudioT;
  ui: WaitMusicUIT;
};

type WaitMusicHandleActionPayloadT =
  | FlowCallGetAllMusicResponseT
  | FlowCallMusicSplitResponseT
  | FlowCallWaitMusicPayloadT
  | any;

type WaitMusicMetaT = {
  getAsyncStatus: AsyncStatus;
  uploadAsyncStatus: AsyncStatus;
};

export type WaitMusicUIT = {
  isNewAudioOpen: boolean;
  isWaitingOpen: boolean;
};

function emptyAudio(): AudioT {
  return {
    chunk_duration_in_secs: 0,
    chunk_urls: [],
    id: '',
    master_file_path: null,
    name: undefined,
  };
}

const initialState: WaitMusicStateT = {
  allAudios: [],
  jobIds: [],
  meta: {
    getAsyncStatus: 'succeeded',
    uploadAsyncStatus: 'succeeded',
  },
  selectedAudio: emptyAudio(),
  ui: {
    isNewAudioOpen: false,
    isWaitingOpen: false,
  },
};

export const {
  waitMusicGetAll,
  waitMusicGetAllSucceeded,
  waitMusicGetAllFailed,
  waitMusicUpload,
  waitMusicUploadSucceeded,
  waitMusicUploadFailed,
  waitMusicSplit,
  waitMusicSplitSucceeded,
  waitMusicSplitFailed,
  waitMusicSelect,
  waitMusicReset,
  waitMusicShowModal,
  waitMusicHideModal,
  waitMusicWsUploadStatus,
  waitMusicWaitingForSplitingOpen,
  waitMusicWaitingForSplitingClose,
} = waitMusicActions;

const waitMusic = handleActions<WaitMusicStateT, WaitMusicHandleActionPayloadT>(
  {
    [waitMusicGetAll.toString()]: (state: WaitMusicStateT): WaitMusicStateT => {
      return { ...state, meta: { ...state.meta, getAsyncStatus: 'loading' } };
    },

    [waitMusicGetAllSucceeded.toString()]: (
      state: WaitMusicStateT,
      action: Action<FlowCallGetAllMusicResponseT>,
    ): WaitMusicStateT => {
      const allAudios: AudioT[] = [];
      for (const waitMusic of action.payload.data.wait_music) {
        allAudios.push({
          ...waitMusic,
          id: waitMusic.uuid,
        });
      }
      return {
        ...state,
        meta: { ...state.meta, getAsyncStatus: 'succeeded' },
        allAudios,
      };
    },

    [waitMusicGetAllFailed.toString()]: (
      state: WaitMusicStateT,
    ): WaitMusicStateT => {
      return { ...state, meta: { ...state.meta, getAsyncStatus: 'failed' } };
    },

    [waitMusicUpload.toString()]: (state: WaitMusicStateT): WaitMusicStateT => {
      return {
        ...state,
        meta: { ...state.meta, uploadAsyncStatus: 'loading' },
      };
    },

    [waitMusicUploadSucceeded.toString()]: (
      state: WaitMusicStateT,
    ): WaitMusicStateT => {
      return {
        ...state,
        meta: { ...state.meta, uploadAsyncStatus: 'succeeded' },
        ui: { ...state.ui, isNewAudioOpen: true },
      };
    },

    [waitMusicUploadFailed.toString()]: (
      state: WaitMusicStateT,
    ): WaitMusicStateT => {
      return { ...state, meta: { ...state.meta, uploadAsyncStatus: 'failed' } };
    },

    [waitMusicSplit.toString()]: (state: WaitMusicStateT): WaitMusicStateT => {
      return {
        ...state,
        meta: { ...state.meta, uploadAsyncStatus: 'loading' },
      };
    },

    [waitMusicSplitSucceeded.toString()]: (
      state: WaitMusicStateT,
      action: Action<FlowCallMusicSplitResponseT>,
    ): WaitMusicStateT => {
      return {
        ...state,
        meta: { ...state.meta, uploadAsyncStatus: 'succeeded' },
        ui: { ...state.ui, isNewAudioOpen: true },
        jobIds: [action.payload.data.job_id, ...state.jobIds],
      };
    },

    [waitMusicSplitFailed.toString()]: (
      state: WaitMusicStateT,
    ): WaitMusicStateT => {
      return { ...state, meta: { ...state.meta, uploadAsyncStatus: 'failed' } };
    },

    [waitMusicWsUploadStatus.toString()]: (
      state: WaitMusicStateT,
      action: any,
    ): WaitMusicStateT => {
      return {
        ...state,
        ui: { ...state.ui, isWaitingOpen: false, isNewAudioOpen: false },
        jobIds: state.jobIds.filter(
          (jobID: string) => jobID !== action.payload.job_id,
        ),
      };
    },

    [waitMusicSelect.toString()]: (
      state: WaitMusicStateT,
      action: Action<FlowCallWaitMusicPayloadT>,
    ): WaitMusicStateT => {
      const selectedAudio = Object.assign({}, action.payload.selectedAudio);
      return { ...state, selectedAudio };
    },

    [waitMusicReset.toString()]: (state: WaitMusicStateT): WaitMusicStateT => {
      return { ...state, selectedAudio: emptyAudio() };
    },

    [waitMusicShowModal.toString()]: (
      state: WaitMusicStateT,
    ): WaitMusicStateT => {
      return { ...state, ui: { ...state.ui, isNewAudioOpen: true } };
    },

    [waitMusicHideModal.toString()]: (
      state: WaitMusicStateT,
    ): WaitMusicStateT => {
      return { ...state, ui: { ...state.ui, isNewAudioOpen: false } };
    },

    [waitMusicWaitingForSplitingOpen.toString()]: (
      state: WaitMusicStateT,
    ): WaitMusicStateT => {
      return { ...state, ui: { ...state.ui, isWaitingOpen: true } };
    },

    [waitMusicWaitingForSplitingClose.toString()]: (
      state: WaitMusicStateT,
    ): WaitMusicStateT => {
      return { ...state, ui: { ...state.ui, isWaitingOpen: false } };
    },
  },
  initialState,
);

export default waitMusic;
